import { useCategories } from '../lib/categories';
import { useSettings } from '../lib/settings';
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Upload, X, LogOut, FileText, Image as ImageIcon, Video } from "lucide-react";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { useLang, localized } from "../lib/i18n";
import { api, FILE_URL, formatApiErrorDetail } from "../lib/api";

const EMPTY_DOOR = {
  name: { az: "", ru: "", en: "" },
  description: { az: "", ru: "", en: "" },
  price: 0,
  category: "entrance",
  material: "",
  dimensions: "",
  colors: [],
  image_id: null,
};



export default function AdminDashboard() {
  const { user, loading, logout, isAdmin } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [tab, setTab] = useState("doors");

  if (!loading && !user) return <Navigate to="/login" replace />;
  if (!loading && user && !isAdmin) return <Navigate to="/profile" replace />;
  if (loading) return <Layout><div className="fd-container py-24 text-center text-neutral-400" data-testid="admin-loading">{t("common.loading")}</div></Layout>;

  return (
    <Layout>
      <section className="fd-container py-8 md:py-12 border-b border-neutral-200" data-testid="admin-header">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="fd-label mb-2">— {t("admin.dashboard")}</p>
            <h1 className="text-3xl md:text-5xl font-light tracking-tight text-neutral-900">Friend Doors</h1>
            <p className="text-sm text-neutral-500 mt-2">{user?.email}</p>
          </div>
          <button onClick={() => { nav("/"); logout(); }} className="fd-btn-ghost text-xs" data-testid="admin-logout">
            <LogOut size={14} /> {t("admin.logout")}
          </button>
        </div>
        <div className="flex gap-1 mt-8 flex-wrap">
          <button onClick={() => setTab("categories")} className={`px-6 py-3 text-sm border-b-2 ${tab === "categories" ? "border-neutral-900" : "border-transparent"}`}>Kateqoriyalar</button>
          <button onClick={() => setTab("doors")} className={`px-6 py-3 text-sm border-b-2 transition-colors ${tab === "doors" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-400 hover:text-neutral-700"}`} data-testid="tab-doors">
            {t("admin.doors_tab")}
          </button>
          <button onClick={() => setTab("catalogs")} className={`px-6 py-3 text-sm border-b-2 transition-colors ${tab === "catalogs" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-400 hover:text-neutral-700"}`} data-testid="tab-catalogs">
            {t("admin.catalogs_tab")}
          </button>
          <button onClick={() => setTab("hero")} className={`px-6 py-3 text-sm border-b-2 transition-colors ${tab === "hero" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-400 hover:text-neutral-700"}`} data-testid="tab-hero">
            {t("admin.hero_tab")}
          </button>
        </div>
      </section>

      <section className="fd-container py-8 md:py-12">
        {tab === "categories" && <CategoriesManager />}
        {tab === "doors" && <DoorsManager />}
        {tab === "catalogs" && <CatalogsManager />}
        {tab === "hero" && <HeroManager />}
      </section>
    </Layout>
  );
}

function DoorsManager() {
  const { t, lang } = useLang();
  const [doors, setDoors] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/doors").then((r) => setDoors(r.data));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try { await api.delete(`/doors/${id}`); toast.success(t("admin.deleted")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <p className="fd-label">{doors.length} items</p>
        <button onClick={() => setEditing(EMPTY_DOOR)} className="fd-btn-primary" data-testid="add-door-btn">
          <Plus size={16} /> {t("admin.add_door")}
        </button>
      </div>

      {doors.length === 0 ? (
        <div className="border border-dashed border-neutral-200 py-16 text-center text-sm text-neutral-400" data-testid="admin-doors-empty">
          {t("admin.no_items")}
        </div>
      ) : (
        <div className="border border-neutral-200" data-testid="admin-doors-table">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="fd-label !text-[10px] py-3 px-4">Image</th>
                <th className="fd-label !text-[10px] py-3 px-4">{t("admin.name")}</th>
                <th className="fd-label !text-[10px] py-3 px-4">{t("admin.category")}</th>
                <th className="fd-label !text-[10px] py-3 px-4">{t("admin.price")}</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {doors.map((d) => (
                <tr key={d.id} className="border-b border-neutral-100" data-testid={`admin-door-row-${d.id}`}>
                  <td className="py-3 px-4">
                    <div className="w-14 h-14 bg-neutral-100 overflow-hidden">
                      {d.image_id && <img src={FILE_URL(d.image_id)} alt="" className="w-full h-full object-cover" />}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-neutral-900">{localized(d.name, lang)}</td>
                  <td className="py-3 px-4 text-neutral-600">{t(`categories.${d.category}`)}</td>
                  <td className="py-3 px-4 text-neutral-900">{d.price} AZN</td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => setEditing(d)} className="p-2 border border-neutral-200 hover:border-neutral-900 transition-colors" data-testid={`edit-door-${d.id}`}><Pencil size={14} /></button>
                      <button onClick={() => onDelete(d.id)} className="p-2 border border-neutral-200 hover:border-red-600 hover:text-red-600 transition-colors" data-testid={`delete-door-${d.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <DoorForm door={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function DoorForm({ door, onClose, onSaved }) {
  const CATS = useCategories();
  const { t } = useLang();
  const [form, setForm] = useState({
    ...door,
    colors: Array.isArray(door.colors) ? door.colors.join(", ") : (door.colors || ""),
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEdit = !!door.id;

  const updateLoc = (field, l, v) => setForm({ ...form, [field]: { ...form[field], [l]: v } });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, image_id: res.data.id });
      toast.success(t("admin.uploaded"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price) || 0,
      category: form.category,
      material: form.material,
      dimensions: form.dimensions,
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      image_id: form.image_id,
    };
    try {
      if (isEdit) await api.put(`/doors/${door.id}`, payload);
      else await api.post("/doors", payload);
      toast.success(t("admin.saved"));
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-start md:items-center justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="door-form-modal">
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <h2 className="text-xl text-neutral-900">{isEdit ? t("admin.edit_door") : t("admin.add_door")}</h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100" data-testid="door-form-close"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-6" data-testid="door-form">
          {/* Image */}
          <div>
            <label className="fd-label block mb-2">{t("admin.image")}</label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 bg-neutral-100 border border-neutral-200 overflow-hidden">
                {form.image_id && <img src={FILE_URL(form.image_id)} alt="" className="w-full h-full object-cover" />}
              </div>
              <label className="fd-btn-ghost cursor-pointer" data-testid="door-image-upload-label">
                <Upload size={14} /> {uploading ? t("common.loading") : t("admin.upload")}
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="door-image-upload" />
              </label>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="fd-label block mb-2">{t("admin.name")}</label>
            <div className="grid grid-cols-3 gap-3">
              {["az", "ru", "en"].map((l) => (
                <input key={l} placeholder={l.toUpperCase()} value={form.name[l]} onChange={(e) => updateLoc("name", l, e.target.value)}
                  required={l === "az"}
                  className="border border-neutral-300 px-3 py-2 focus:border-neutral-900 outline-none" data-testid={`door-name-${l}`} />
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="fd-label block mb-2">{t("admin.description")}</label>
            <div className="grid grid-cols-3 gap-3">
              {["az", "ru", "en"].map((l) => (
                <textarea key={l} placeholder={l.toUpperCase()} value={form.description[l]} onChange={(e) => updateLoc("description", l, e.target.value)}
                  rows={3} className="border border-neutral-300 px-3 py-2 focus:border-neutral-900 outline-none resize-none" data-testid={`door-description-${l}`} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="fd-label block mb-2">{t("admin.price")}</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required
                className="w-full border border-neutral-300 px-3 py-2 focus:border-neutral-900 outline-none" data-testid="door-price" />
            </div>
            <div>
              <label className="fd-label block mb-2">{t("admin.category")}</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-neutral-300 px-3 py-2 focus:border-neutral-900 outline-none bg-white" data-testid="door-category">
                {CATS.map((c) => <option key={c} value={c}>{t(`categories.${c}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="fd-label block mb-2">{t("admin.material")}</label>
              <input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })}
                className="w-full border border-neutral-300 px-3 py-2 focus:border-neutral-900 outline-none" data-testid="door-material" />
            </div>
            <div>
              <label className="fd-label block mb-2">{t("admin.dimensions")}</label>
              <input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })}
                className="w-full border border-neutral-300 px-3 py-2 focus:border-neutral-900 outline-none" data-testid="door-dimensions" />
            </div>
            <div className="md:col-span-2">
              <label className="fd-label block mb-2">{t("admin.colors")}</label>
              <input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Ağ, Qara, Palıd"
                className="w-full border border-neutral-300 px-3 py-2 focus:border-neutral-900 outline-none" data-testid="door-colors" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
            <button type="button" onClick={onClose} className="fd-btn-ghost" data-testid="door-form-cancel">{t("admin.cancel")}</button>
            <button type="submit" disabled={saving} className="fd-btn-primary" data-testid="door-form-save">{saving ? t("common.loading") : t("admin.save")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CatalogsManager() {
  const { t, lang } = useLang();
  const [catalogs, setCatalogs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/catalogs").then((r) => setCatalogs(r.data));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try { await api.delete(`/catalogs/${id}`); toast.success(t("admin.deleted")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <p className="fd-label">{catalogs.length} PDF</p>
        <button onClick={() => setCreating(true)} className="fd-btn-primary" data-testid="add-catalog-btn">
          <Plus size={16} /> {t("admin.add_catalog")}
        </button>
      </div>

      {catalogs.length === 0 ? (
        <div className="border border-dashed border-neutral-200 py-16 text-center text-sm text-neutral-400" data-testid="admin-catalogs-empty">{t("admin.no_items")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-catalogs-list">
          {catalogs.map((c) => (
            <div key={c.id} className="border border-neutral-200 p-5 flex items-center justify-between gap-4" data-testid={`admin-catalog-${c.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-12 border border-neutral-300 flex items-center justify-center shrink-0 overflow-hidden bg-neutral-50">
                  {c.cover_id
                    ? <img src={FILE_URL(c.cover_id)} alt="" className="w-full h-full object-cover" />
                    : <FileText size={18} className="text-neutral-700" />}
                </div>
                <div className="min-w-0">
                  <p className="text-neutral-900 truncate">{localized(c.title, lang)}</p>
                  <a href={FILE_URL(c.file_id)} target="_blank" rel="noreferrer" className="text-xs text-neutral-500 hover:text-neutral-900">preview</a>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setEditing(c)} className="p-2 border border-neutral-200 hover:border-neutral-900 hover:text-neutral-900 transition-colors" data-testid={`edit-catalog-${c.id}`}><Pencil size={14} /></button>
                <button onClick={() => onDelete(c.id)} className="p-2 border border-neutral-200 hover:border-red-600 hover:text-red-600 transition-colors" data-testid={`delete-catalog-${c.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <CatalogForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <CatalogForm catalog={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function CatalogForm({ catalog, onClose, onSaved }) {
  const { t } = useLang();
  const isEdit = !!catalog;
  const [title, setTitle] = useState(catalog?.title || { az: "", ru: "", en: "" });
  const [fileId, setFileId] = useState(catalog?.file_id || null);
  const [fileName, setFileName] = useState("");
  const [coverId, setCoverId] = useState(catalog?.cover_id || null);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [saving, setSaving] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(res.data.id);
      setFileName(file.name);
      toast.success(t("admin.uploaded"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const onUploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCoverId(res.data.id);
      toast.success(t("admin.uploaded"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploadingCover(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!fileId) { toast.error(t("admin.upload_pdf_first")); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/catalogs/${catalog.id}`, { title, file_id: fileId, cover_id: coverId });
      } else {
        await api.post("/catalogs", { title, file_id: fileId, cover_id: coverId });
      }
      toast.success(t("admin.saved"));
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="catalog-form-modal">
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <h2 className="text-xl text-neutral-900">{isEdit ? t("admin.edit") : t("admin.add_catalog")}</h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100" data-testid="catalog-form-close"><X size={18} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-6" data-testid="catalog-form">
          <div>
            <label className="fd-label block mb-2">{t("admin.title_field")}</label>
            <div className="grid grid-cols-3 gap-3">
              {["az", "ru", "en"].map((l) => (
                <input key={l} placeholder={l.toUpperCase()} value={title[l]} onChange={(e) => setTitle({ ...title, [l]: e.target.value })} required={l === "az"}
                  className="border border-neutral-300 px-3 py-2 focus:border-neutral-900 outline-none" data-testid={`catalog-title-${l}`} />
              ))}
            </div>
          </div>
          <div>
            <label className="fd-label block mb-2">{t("admin.pdf_file")}</label>
            <div className="flex items-center gap-4">
              <label className="fd-btn-ghost cursor-pointer" data-testid="catalog-file-label">
                <Upload size={14} /> {uploading ? t("common.loading") : t("admin.upload")}
                <input type="file" accept="application/pdf" onChange={onUpload} className="hidden" data-testid="catalog-file-upload" />
              </label>
              {(fileName || fileId) && <span className="text-sm text-neutral-500 truncate">{fileName || "PDF ✓"}</span>}
            </div>
          </div>
          <div>
            <label className="fd-label block mb-2">{t("admin.cover_image")}</label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-20 border border-neutral-300 flex items-center justify-center shrink-0 overflow-hidden bg-neutral-50">
                {coverId
                  ? <img src={FILE_URL(coverId)} alt="cover" className="w-full h-full object-cover" data-testid="catalog-cover-preview" />
                  : <ImageIcon size={18} className="text-neutral-400" />}
              </div>
              <div className="space-y-2">
                <label className="fd-btn-ghost cursor-pointer inline-flex" data-testid="catalog-cover-label">
                  <Upload size={14} /> {uploadingCover ? t("common.loading") : t("admin.upload_image")}
                  <input type="file" accept="image/*" onChange={onUploadCover} className="hidden" data-testid="catalog-cover-upload" />
                </label>
                {coverId && (
                  <button type="button" onClick={() => setCoverId(null)} className="block text-xs text-red-600 hover:underline" data-testid="catalog-cover-remove">{t("admin.remove")}</button>
                )}
              </div>
            </div>
            <p className="text-xs text-neutral-400 mt-2">{t("admin.cover_help")}</p>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
            <button type="button" onClick={onClose} className="fd-btn-ghost" data-testid="catalog-form-cancel">{t("admin.cancel")}</button>
            <button type="submit" disabled={saving} className="fd-btn-primary" data-testid="catalog-form-save">{saving ? t("common.loading") : t("admin.save")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


function HeroManager() {
  const { t } = useLang();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingVid, setUploadingVid] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).finally(() => setLoading(false));
  }, []);

  const uploadMedia = async (file, setUploading, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...(s || {}), [field]: res.data.id }));
      toast.success(t("admin.uploaded"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.put("/settings", {
        hero_image_id: settings.hero_image_id || null,
        hero_video_id: settings.hero_video_id || null,
      });
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const clearField = (field) => setSettings((s) => ({ ...(s || {}), [field]: null }));

  if (loading) return <div className="text-neutral-400 text-sm">{t("common.loading")}</div>;

  return (
    <div className="max-w-3xl space-y-10" data-testid="hero-manager">
      <div>
        <p className="fd-label mb-2">— {t("admin.hero_tab")}</p>
        <h2 className="text-2xl md:text-3xl font-light tracking-tight text-neutral-900">{t("admin.hero_title_section")}</h2>
        <p className="text-sm text-neutral-500 mt-2">{t("admin.hero_help")}</p>
      </div>

      {/* Image */}
      <div className="border border-neutral-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon size={16} className="text-neutral-700" />
          <p className="text-sm text-neutral-900">{t("admin.hero_image")}</p>
        </div>
        {settings?.hero_image_id ? (
          <div className="relative w-48 aspect-[4/5] bg-neutral-100 overflow-hidden mb-4">
            <img src={FILE_URL(settings.hero_image_id)} alt="hero" className="w-full h-full object-cover" />
            <button onClick={() => clearField("hero_image_id")} className="absolute top-2 right-2 p-1 bg-white/90 hover:bg-white border border-neutral-200" data-testid="hero-img-remove"><X size={14} /></button>
          </div>
        ) : (
          <p className="text-xs text-neutral-400 mb-4">{t("admin.no_image")}</p>
        )}
        <label className="fd-btn-ghost cursor-pointer inline-flex" data-testid="hero-image-label">
          <Upload size={14} /> {uploadingImg ? t("common.loading") : t("admin.upload_image")}
          <input type="file" accept="image/*" onChange={(e) => uploadMedia(e.target.files?.[0], setUploadingImg, "hero_image_id")} className="hidden" data-testid="hero-image-upload" />
        </label>
      </div>

      {/* Video */}
      <div className="border border-neutral-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Video size={16} className="text-neutral-700" />
          <p className="text-sm text-neutral-900">{t("admin.hero_video")}</p>
        </div>
        {settings?.hero_video_id ? (
          <div className="relative w-64 aspect-[4/5] bg-neutral-100 overflow-hidden mb-4">
            <video src={FILE_URL(settings.hero_video_id)} muted loop autoPlay playsInline className="w-full h-full object-cover" />
            <button onClick={() => clearField("hero_video_id")} className="absolute top-2 right-2 p-1 bg-white/90 hover:bg-white border border-neutral-200" data-testid="hero-vid-remove"><X size={14} /></button>
          </div>
        ) : (
          <p className="text-xs text-neutral-400 mb-4">{t("admin.no_video")}</p>
        )}
        <label className="fd-btn-ghost cursor-pointer inline-flex" data-testid="hero-video-label">
          <Upload size={14} /> {uploadingVid ? t("common.loading") : t("admin.upload_video")}
          <input type="file" accept="video/mp4,video/webm,video/quicktime,video/*" onChange={(e) => uploadMedia(e.target.files?.[0], setUploadingVid, "hero_video_id")} className="hidden" data-testid="hero-video-upload" />
        </label>
        <p className="text-[11px] text-neutral-400 mt-3">{t("admin.hero_video_note")}</p>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
        <button type="button" onClick={save} disabled={saving} className="fd-btn-primary" data-testid="hero-save">
          {saving ? t("common.loading") : t("admin.save")}
        </button>
      </div>
    </div>
  );
}

function CategoriesManager() {
 const { refresh } = useSettings();
 const { t } = useLang();
 const categories = useCategories();
 const [name, setName] = useState('');
 const [busy, setBusy] = useState(false);
 const add = async (e) => {
  e.preventDefault(); const label = name.trim(); if (!label || busy) return;
  if (categories.some(id => t('categories.' + id).toLocaleLowerCase('az') === label.toLocaleLowerCase('az'))) { toast.error('Bu kateqoriya artıq mövcuddur'); return; }
  setBusy(true);
  try {
   const current = await api.get('/settings');
   await api.put('/settings', { categories: [...(current.data.categories || []), { id: 'cat_' + crypto.randomUUID(), az: label }] });
   await refresh(); setName(''); toast.success('Kateqoriya əlavə edildi');
  } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail || e.message)); }
  finally { setBusy(false); }
 };
 return <div className="max-w-xl"><h2 className="text-2xl font-medium mb-6">Kateqoriyalar</h2><form onSubmit={add} className="flex gap-3 mb-8"><input aria-label="Yeni kateqoriyanın adı" placeholder="Yeni kateqoriyanın adı" value={name} onChange={e => setName(e.target.value)} maxLength={80} required className="min-w-0 flex-1 border border-neutral-300 px-4 py-3"/><button disabled={busy || !name.trim()} className="fd-btn-primary disabled:opacity-50">{busy ? 'Əlavə edilir…' : 'Əlavə et'}</button></form><div className="divide-y border border-neutral-200">{categories.map(id => <div key={id} className="px-5 py-4">{t('categories.' + id)}</div>)}</div></div>;
}
